import type jsPDF from "jspdf";
import { AiChatMessage, AiSource } from "../../types/Ai";
import { ResearchFigureSpec, ResearchTable } from "../../types/bd/Research";
import { DocModel, docxBlob, figureImages, newModel, para, renumber } from "../bd/bdDocModel";
import { cellText, fileSafe, plainColumns, rasterizeFigure } from "../bd/researchExport";
import {
    applyBrandedPageFooter, createPdfDocument, drawPdfSectionTitle, ensurePdfSpace, loadPdfFonts, PDF_FOOTER_RESERVE, PDF_MARGIN_X, pdfColors, pdfDrawable,
    renderPdfTable,
} from "../exportToPdfHelper";
import { saveBlob } from "../request";
import { AskChatDetail, AskChatMaterial, chatTitle } from "./askChat";
import { askFigure } from "./askFigure";

/**
 * A conversation as a Word file or a PDF: its record, the files read in it,
 * then every question and answer in order with the answer's sources, tables
 * and pictures (taken from the screen as drawn, so the chat must be open).
 */

/** The SVG id AskFigures gives a figure: the answer's message id and the figure's place in the answer. */
export type SvgIdFor = (messageId: number, index: number) => string;
export const askFigureId: SvgIdFor = (messageId, index) => `ask-figure-${messageId}-${index}`;

const NOTE = "Names, emails, phone numbers and amounts were hidden from the AI. Parts marked “AI's own knowledge” are not from the files or CRX: check them before you rely on them.";
const isWebLink = (link: string): boolean => /^https?:\/\//i.test(link);
const aboutLine = (detail: AskChatDetail): string => (detail.linked ? `About ${detail.linked.name}` : "Not about a CRX record");
const materialLine = (material: AskChatMaterial): string =>
    [material.name, material.parts, material.publicFigures ? "figures visible" : "figures hidden", material.sourceUrl ?? ""].filter(Boolean).join(" · ");
/** "Sources: Reuters (https://…) · Mission MSN-0142". */
const sourcesLine = (sources: AiSource[]): string => `Sources: ${sources.map((s) => (isWebLink(s.link) ? `${s.label} (${s.link})` : s.label)).join(" · ")}`;
/** The question as a heading, with the file or text that went with it. */
const questionText = (message: AiChatMessage): string =>
    [message.text?.trim(), message.material?.name ?? message.attachment].filter(Boolean).join(" · ") || "Attached a file";

const bulletOf = (line: string): string | null => /^([-•*]|\d+[.)])\s+(.*)$/.exec(line.trim())?.[2] ?? null;

const wordTable = (m: DocModel, t: ResearchTable) => {
    const plain = plainColumns(t);
    para(m, "Normal", t.title, { bold: [[0, t.title.length]] });
    para(m, "Normal", "", {
        table: {
            columns: t.columns,
            rows: t.rows.map((r) => t.columns.map((_c, i) => cellText(r[i], plain[i]))),
            numeric: t.columns.map((_c, i) => t.rows.length > 0 && t.rows.every((r) => typeof r[i] === "number" || r[i] === "")),
        },
    });
};

/** The conversation as a Word model. */
export function buildConversationModel(detail: AskChatDetail, today: string, svgIdFor: SvgIdFor = askFigureId): DocModel {
    const title = chatTitle(detail);
    const m = newModel(title);
    para(m, "Title", title);
    para(m, "Subtitle", `Ask CRX conversation · ${aboutLine(detail)} · ${today}`);
    para(m, "Normal", NOTE);
    if (detail.materials.length) {
        para(m, "Heading1", "Files in this conversation");
        detail.materials.forEach((material) => para(m, "ListBullet", materialLine(material)));
    }
    detail.messages.forEach((message) => {
        if (message.role === "user") {
            para(m, "Heading2", questionText(message));
            return;
        }
        (message.text ?? "").split(/\r?\n/).map((line) => line.replace(/\*\*/g, "").trim()).filter((line) => line && !FIGURE_MARK.test(line) && !DOWNLOAD_LINE.test(line)).forEach((line) => {
            const bullet = bulletOf(line);
            para(m, bullet ? "ListBullet" : "Normal", bullet ?? line);
        });
        (message.figures ?? []).forEach((raw, index) => {
            const figure = askFigure(raw);
            if (!figure) return;
            if (figure.type === "table") wordTable(m, figure);
            else para(m, "Figure", "", { figure: { svgId: svgIdFor(message.messageId, index), spec: figure as ResearchFigureSpec } });
        });
        if (message.sources?.length) para(m, "Normal", sourcesLine(message.sources), { bold: [[0, 8]] });
    });
    return renumber(m);
}

/** "## Purpose", "# Result" or a line that is only "**Conclusion**" opens a section of a finished document. */
export const headingOf = (line: string): { level: 1 | 2; text: string } | null => {
    const marked = /^(#{1,3})\s+(.+)$/.exec(line);
    // "## " is how answers mark a report's sections (the AI is told so): those are the document's top headings
    if (marked) return { level: marked[1].length <= 2 ? 1 : 2, text: marked[2].replace(/\*\*/g, "").replace(/:\s*$/, "").trim() };
    const bold = /^\*\*([^*]{2,80})\*\*:?$/.exec(line);
    return bold ? { level: 1, text: bold[1].replace(/:\s*$/, "").trim() } : null;
};

const longDate = () => new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/** "[Figure 2]" on a line of its own: where the answer's second table or picture goes (the AI is told to mark them). */
export const FIGURE_MARK = /^\[(?:figure|table|chart)\s+(\d{1,2})\]$/i;
/** The closing line Ask adds under a finished document: for the screen, never the file. */
export const DOWNLOAD_LINE = /^download it (?:with|using) (?:the )?download\b.*under this answer\.?$/i;

export type AnswerPart =
    | { kind: "heading"; level: 1 | 2; text: string }
    | { kind: "bullet"; text: string }
    | { kind: "text"; text: string }
    | { kind: "figure"; index: number };

/**
 * A finished answer as a document: its own "# " title when it starts with one,
 * then its sections and text with each table or picture where its "[Figure n]"
 * line stands. Figures with no mark follow at the end; the download hint goes.
 */
export function answerDocument(message: AiChatMessage): { title: string | null; parts: AnswerPart[] } {
    const figureCount = (message.figures ?? []).length;
    const placed = new Set<number>();
    const parts: AnswerPart[] = [];
    let title: string | null = null;
    (message.text ?? "").split(/\r?\n/).map((line) => line.trim()).filter((line) => line && !DOWNLOAD_LINE.test(line)).forEach((line) => {
        const own = /^#\s+(.+)$/.exec(line);
        if (own && title === null && !parts.length) { title = own[1].replace(/\*\*/g, "").trim(); return; }
        const mark = FIGURE_MARK.exec(line);
        if (mark) {
            const index = Number(mark[1]) - 1;
            if (index >= 0 && index < figureCount && !placed.has(index)) { placed.add(index); parts.push({ kind: "figure", index }); }
            return;
        }
        const heading = headingOf(line);
        if (heading) { parts.push({ kind: "heading", level: heading.level, text: heading.text }); return; }
        const clean = line.replace(/\*\*/g, "");
        const bullet = bulletOf(clean);
        parts.push(bullet ? { kind: "bullet", text: bullet } : { kind: "text", text: clean });
    });
    // a figure the AI didn't mark goes where a reader looks for it: a table at the end of the comparables or data
    // section, a chart or range at the end of the result section; anything else at the end
    for (let index = 0; index < figureCount; index++) {
        if (placed.has(index)) continue;
        const type = askFigure((message.figures ?? [])[index])?.type;
        const home = type === "table" ? /comparab|data|figures|table/i : type ? /result|finding|analysis|position/i : null;
        const at = home ? parts.findIndex((part) => part.kind === "heading" && home.test(part.text)) : -1;
        if (at < 0) { parts.push({ kind: "figure", index }); continue; }
        let end = at + 1;
        while (end < parts.length && parts[end].kind !== "heading") end++;
        parts.splice(end, 0, { kind: "figure", index });
    }
    return { title, parts };
}

/** The name a finished answer goes by: its own "# " title, else the conversation's. */
export const answerTitle = (message: AiChatMessage, fallback: string): string => answerDocument(message).title || fallback;

/**
 * One answer as a finished document to send: its title, the date, the sections
 * with tables and pictures in place, and the sources listed at the end. No
 * questions, no file list, no Ask CRX notes.
 */
export function buildAnswerModel(fallbackTitle: string, message: AiChatMessage, today: string, svgIdFor: SvgIdFor = askFigureId): DocModel {
    const { title, parts } = answerDocument(message);
    const name = title || fallbackTitle;
    const m = newModel(name);
    para(m, "Title", name);
    para(m, "Subtitle", today);
    parts.forEach((part) => {
        if (part.kind === "heading") para(m, part.level === 1 ? "Heading1" : "Heading2", part.text);
        else if (part.kind === "bullet") para(m, "ListBullet", part.text);
        else if (part.kind === "text") para(m, "Normal", part.text);
        else {
            const figure = askFigure((message.figures ?? [])[part.index]);
            if (!figure) return;
            if (figure.type === "table") wordTable(m, figure);
            else para(m, "Figure", "", { figure: { svgId: svgIdFor(message.messageId, part.index), spec: figure as ResearchFigureSpec } });
        }
    });
    if (message.sources?.length) {
        para(m, "Heading1", "Sources");
        message.sources.forEach((s) => para(m, "ListBullet", isWebLink(s.link) ? `${s.label}: ${s.link}` : s.label));
    }
    return renumber(m);
}

/** Download one answer as a Word file to edit and send. */
export async function downloadAnswerWord(fallbackTitle: string, message: AiChatMessage, svgIdFor: SvgIdFor = askFigureId): Promise<void> {
    const m = buildAnswerModel(fallbackTitle, message, longDate(), svgIdFor);
    saveBlob(await docxBlob(m, "CRX", await figureImages(m)), `${fileSafe(answerTitle(message, fallbackTitle))}.docx`);
}

/** Download one answer as a PDF to send. */
export async function downloadAnswerPdf(fallbackTitle: string, message: AiChatMessage, svgIdFor: SvgIdFor = askFigureId): Promise<void> {
    await loadPdfFonts();
    const { title, parts } = answerDocument(message);
    const name = title || fallbackTitle;
    const fileName = `${fileSafe(name)}.pdf`;
    const { doc, yPos } = createPdfDocument({
        fileName, orientation: "portrait",
        header: { title: pdfDrawable(name), eyebrow: "Report", subtitle: pdfDrawable(longDate()) },
        runningHeader: { title: pdfDrawable(name) },
    });
    let y = yPos;
    for (const part of parts) {
        if (part.kind === "heading") y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: pdfDrawable(part.text) });
        else if (part.kind === "bullet") y = paragraph(doc, y, `•  ${part.text}`, { indent: 2 });
        else if (part.kind === "text") y = paragraph(doc, y, part.text);
        else {
            const figure = askFigure((message.figures ?? [])[part.index]);
            if (figure) y = figure.type === "table" ? pdfTable(doc, y, figure) : await pdfFigure(doc, y, svgIdFor(message.messageId, part.index));
        }
    }
    if (message.sources?.length) {
        y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: "Sources" });
        message.sources.forEach((s) => { y = paragraph(doc, y, `•  ${isWebLink(s.link) ? `${s.label}: ${s.link}` : s.label}`, { indent: 2 }); });
    }
    applyBrandedPageFooter(doc, { centerText: pdfDrawable(name) });
    saveBlob(doc.output("blob"), fileName);
}

/** Download the conversation as a Word file anyone can edit. */
export async function downloadConversationWord(detail: AskChatDetail, svgIdFor: SvgIdFor = askFigureId): Promise<void> {
    const m = buildConversationModel(detail, new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }), svgIdFor);
    saveBlob(await docxBlob(m, "CRX", await figureImages(m)), `${fileSafe(chatTitle(detail))}.docx`);
}

const BODY_SIZE = 9.2;

/** Wrapped body text from y; returns the y below it. */
const paragraph = (doc: jsPDF, y: number, text: string, opts: { size?: number; color?: [number, number, number]; bold?: boolean; indent?: number } = {}): number => {
    const size = opts.size ?? BODY_SIZE;
    const line = size * 0.3528 * 1.45;
    const width = doc.internal.pageSize.getWidth() - PDF_MARGIN_X * 2 - (opts.indent ?? 0);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(size);
    const lines: string[] = doc.splitTextToSize(pdfDrawable(text), width);
    let at = y;
    lines.forEach((l) => {
        at = ensurePdfSpace(doc, at, line);
        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(...(opts.color ?? pdfColors.body));
        doc.text(l, PDF_MARGIN_X + (opts.indent ?? 0), at + line * 0.75);
        at += line;
    });
    return at + 1.5;
};

const pdfTable = (doc: jsPDF, y: number, t: ResearchTable): number => {
    let at = ensurePdfSpace(doc, y, 18);
    at = paragraph(doc, at, t.title, { bold: true, size: 9, color: pdfColors.strong });
    const plain = plainColumns(t);
    const end = renderPdfTable(doc, { columns: t.columns.map((c) => ({ header: pdfDrawable(c) })), body: t.rows.map((r) => t.columns.map((_c, i) => pdfDrawable(cellText(r[i], plain[i])))), startY: at });
    return end + 5;
};

/** A drawn picture, as wide as the page allows; returns the y below it. */
const pdfFigure = async (doc: jsPDF, y: number, svgId: string): Promise<number> => {
    const drawn = await rasterizeFigure(svgId);
    if (!drawn) return y;
    const contentW = doc.internal.pageSize.getWidth() - PDF_MARGIN_X * 2;
    const maxH = doc.internal.pageSize.getHeight() - PDF_FOOTER_RESERVE - 30;
    let w = Math.min(contentW, drawn.width * 0.26);
    let h = w * (drawn.height / drawn.width);
    if (h > maxH) { h = maxH; w = h * (drawn.width / drawn.height); }
    const at = ensurePdfSpace(doc, y, h + 4);
    doc.addImage(drawn.canvas.toDataURL("image/png"), "PNG", PDF_MARGIN_X, at, w, h);
    return at + h + 5;
};

/** Download the conversation as one PDF in the CRX style. */
export async function downloadConversationPdf(detail: AskChatDetail, svgIdFor: SvgIdFor = askFigureId): Promise<void> {
    await loadPdfFonts();
    const title = chatTitle(detail);
    const fileName = `${fileSafe(title)}.pdf`;
    const { doc, yPos } = createPdfDocument({
        fileName, orientation: "portrait",
        header: { title: pdfDrawable(title), eyebrow: "Ask CRX", subtitle: pdfDrawable(aboutLine(detail)) },
        runningHeader: { title: pdfDrawable(title) },
    });
    let y = paragraph(doc, yPos, NOTE, { size: 8, color: pdfColors.muted });
    if (detail.materials.length) {
        y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: "Files in this conversation", count: detail.materials.length });
        detail.materials.forEach((material) => { y = paragraph(doc, y, `•  ${materialLine(material)}`, { indent: 2 }); });
    }
    y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: "Questions and answers" });
    for (const message of detail.messages) {
        if (message.role === "user") {
            y = paragraph(doc, ensurePdfSpace(doc, y + 2, 16), questionText(message), { bold: true, size: 10, color: pdfColors.strong });
            continue;
        }
        for (const line of (message.text ?? "").split(/\r?\n/).map((l) => l.replace(/\*\*/g, "").trim()).filter((l) => l && !FIGURE_MARK.test(l) && !DOWNLOAD_LINE.test(l))) {
            const bullet = bulletOf(line);
            y = paragraph(doc, y, bullet ? `•  ${bullet}` : line, bullet ? { indent: 2 } : {});
        }
        let index = 0;
        for (const raw of message.figures ?? []) {
            const figure = askFigure(raw);
            if (!figure) { index += 1; continue; }
            y = figure.type === "table" ? pdfTable(doc, y, figure) : await pdfFigure(doc, y, svgIdFor(message.messageId, index));
            index += 1;
        }
        if (message.sources?.length) y = paragraph(doc, y, sourcesLine(message.sources), { size: 7.5, color: pdfColors.muted });
        y += 2;
    }
    applyBrandedPageFooter(doc, { centerText: "Ask CRX · Internal" });
    saveBlob(doc.output("blob"), fileName);
}
